import { useUser, UserButton, SignedIn, SignedOut, SignInButton } from "@clerk/clerk-react";
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { checkUser } from '../service/api/AuthService';
import { getDomains } from "../service/api/domainService";
import { toast } from "sonner";
import DomainLogo from "./DomainLogo";
import DomainSelector from "./DomainSelector"; // ← nouveau composant
import { useNotifications } from "../service/Hooks/useNotifications";

export function Header() {
  const { user } = useUser();
  const { t } = useTranslation(['header', 'common']);
  const [role, setRole] = useState(null);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainList, setDomainList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    formatNotificationTime,
  } = useNotifications();

  useEffect(() => {
    async function fetchAllDomains() {
      try {
        const data = await getDomains();
        setDomainList(data);
      } catch (error) {
        // silencieux
      }
    }
    fetchAllDomains();
  }, []);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        const email = user.emailAddresses[0]?.emailAddress;
        const userDataResponse = await checkUser(email);
        setUserData(userDataResponse);

        if (userDataResponse?.domains) {
          setDomains(userDataResponse.domains);
          sessionStorage.setItem("domains", JSON.stringify(userDataResponse.domains));
        }

        if (userDataResponse?.isAdmin) {
          setRole("admin");
          setSelectedDomain(null);
          sessionStorage.removeItem("selectedDomain");
        } else {
          const storedDomain = sessionStorage.getItem("selectedDomain");
          if (storedDomain) {
            const domainObj = JSON.parse(storedDomain);
            if (domainObj) {
              setSelectedDomain(domainObj);
              setRole(determineFullRole(domainObj));
            }
          } else if (userDataResponse?.domains?.length > 0) {
            const firstDomain = userDataResponse.domains[0];
            setSelectedDomain(firstDomain);
            setRole(determineFullRole(firstDomain));
            sessionStorage.setItem("selectedDomain", JSON.stringify(firstDomain));
          } else {
            setRole("manager");
          }
        }
      } catch (error) {
        setRole("manager");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const determineFullRole = (domain) => {
    if (!domain) return "manager";
    if (domain.isValidator && domain.isValidationManager) return "validateurManager";
    if (domain.isValidator) return "validateur";
    if (domain.isValidationManager) return "validateurManager";
    return domain.role || "manager";
  };

  // handleDomainChange accepte maintenant soit un event (depuis <select>)
  // soit un objet { target: { value: id } } depuis DomainSelector
  const handleDomainChange = (e) => {
    const newDomainId = parseInt(e.target.value, 10);
    const newDomain = domains.find((d) => d.id === newDomainId);

    if (newDomain) {
      setSelectedDomain(newDomain);
      const fullRole = determineFullRole(newDomain);
      setRole(fullRole);
      sessionStorage.setItem("selectedDomain", JSON.stringify(newDomain));

      let url = '#';
      switch (fullRole) {
        case "admin":
          url = `/Trade-Dashboard-admin`;
          break;
        case "manager":
        case "validateurManager":
          url = `/Trade-Dashboard-manager`;
          break;
        case "validateur":
          url = `/page/app/Component/admin/admin_trade_vault`;
          break;
        case "client":
          url = '/page/app/customer/dashboard_customer_trade_vault';
          break;
        default:
          url = '#';
      }

      window.location.href = url;
    }
  };

  const handleNotificationClick = async (notification) => {
    await markAsRead(notification.Id);
    if (notification.ActionUrl) {
      window.location.href = notification.ActionUrl;
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    toast.success(t('notifications.markAllAsRead'));
  };

  const getRoleDisplay = (domain) => {
    if (!domain) return t(`roles.${role}`, { defaultValue: role });
    const fullRole = determineFullRole(domain);
    return t(`roles.${fullRole}`, { defaultValue: fullRole });
  };

  if (loading) {
    return (
      <div className="header">
        <div className="header-content">
          <div className="d-flex justify-content-between align-items-center p-3">
            <div className="placeholder-glow">
              <div className="placeholder col-6 bg-secondary" style={{ height: '30px' }}></div>
            </div>
            <div className="placeholder-glow">
              <div className="placeholder rounded-circle bg-secondary" style={{ width: '45px', height: '45px' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="header">
      <div className="header-content">
        <nav className="navbar navbar-expand">
          <div className="collapse navbar-collapse justify-content-between">
            {/* Section gauche */}
            <div className="d-flex align-items-center" style={{ minHeight: '80px' }}>
              {selectedDomain ? (
                <div className="d-flex align-items-center" style={{ gap: '1rem', minWidth: '0' }}>
                  <DomainLogo
                    blobName={selectedDomain.photo}
                    domainName={selectedDomain.name}
                    size={60}
                  />
                  <div className="d-flex flex-column" style={{ minWidth: '0' }}>
                    <h5
                      className="mb-0 fw-bold text-primary text-uppercase text-truncate"
                      style={{ fontSize: '1.80rem', lineHeight: '1.2' }}
                    >
                      {selectedDomain.name}
                    </h5>
                    <small className="text-muted text-capitalize">
                      {getRoleDisplay(selectedDomain)}
                    </small>
                  </div>
                </div>
              ) : role === "admin" ? (
                <div className="d-flex align-items-center" style={{ gap: '1rem', minWidth: '0' }}>
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white"
                    style={{ width: '60px', height: '60px' }}
                  >
                    <span className="fw-bold" style={{ fontSize: '1.5rem' }}>A</span>
                  </div>
                  <div className="d-flex flex-column" style={{ minWidth: '0' }}>
                    <h5
                      className="mb-0 fw-bold text-primary text-uppercase text-truncate"
                      style={{ fontSize: '1.80rem', lineHeight: '1.2' }}
                    >
                      {t('roles.admin')}
                    </h5>
                    <small className="text-muted">
                      {user?.fullName || user?.emailAddresses[0]?.emailAddress}
                    </small>
                  </div>
                </div>
              ) : (
                <div style={{ width: '60px', height: '60px' }}></div>
              )}
            </div>

            {/* Section droite */}
            <ul className="navbar-nav header-right">

              {/* ✅ Sélecteur de domaine redesigné — remplace l'ancien <select> */}
              <DomainSelector
                domains={domains}
                selectedDomain={selectedDomain}
                onDomainChange={handleDomainChange}
                getRoleDisplay={getRoleDisplay}
              />

              {/* Domaine unique (affichage simple) */}
              {domains.length === 1 && !role && (
                <li className="nav-item d-flex align-items-center">
                  <span className="text-muted me-2">
                    {domains[0].name} ({getRoleDisplay(domains[0])})
                  </span>
                </li>
              )}

              {/* Notifications */}
              <li className="nav-item dropdown notification_dropdown">
                <button
                  className="nav-link position-relative btn btn-link"
                  data-bs-toggle="dropdown"
                  aria-label={t('notifications.title')}
                >
                  <svg width={23} height={23} viewBox="0 0 26 26" fill="none">
                    <path d="M22.75 10.8334C22.7469 9.8751 22.4263 8.94488 21.8382 8.18826C21.2501 7.43163 20.4279 6.89126 19.5 6.6517V4.33337C19.4997 4.15871 19.4572 3.98672 19.3761 3.83204C19.295 3.67736 19.1777 3.54459 19.0342 3.44503C18.8922 3.34623 18.7286 3.28286 18.5571 3.26024C18.3856 3.23763 18.2111 3.25641 18.0484 3.31503L8.59086 6.7492L4.39835 6.50003C4.25011 6.49047 4.10147 6.51151 3.9617 6.56183C3.82192 6.61215 3.69399 6.69068 3.58585 6.79253C3.4789 6.89448 3.39394 7.01723 3.33619 7.15323C3.27843 7.28924 3.24911 7.43561 3.25002 7.58337V15.1667C3.25022 15.3205 3.28316 15.4725 3.34667 15.6126C3.41018 15.7527 3.5028 15.8777 3.61835 15.9792C3.733 16.0795 3.86752 16.1545 4.01312 16.1993C4.15873 16.2441 4.31214 16.2577 4.46335 16.2392L5.88252 16.0659L6.90085 21.8509C6.94471 22.1052 7.07794 22.3356 7.27655 22.5004C7.47516 22.6653 7.7261 22.7538 7.98419 22.75H11.9167C12.0748 22.7521 12.2314 22.7195 12.3756 22.6545C12.5197 22.5896 12.648 22.4939 12.7512 22.3741C12.8544 22.2544 12.9302 22.1135 12.9732 21.9613C13.0162 21.8092 13.0253 21.6494 13 21.4934L12.1984 16.7267L18.1242 18.4167C18.2211 18.4325 18.3198 18.4325 18.4167 18.4167C18.704 18.4167 18.9796 18.3026 19.1827 18.0994C19.3859 17.8962 19.5 17.6207 19.5 17.3334V15.015C20.4279 14.7755 21.2501 14.2351 21.8382 13.4785C22.4263 12.7218 22.7469 11.7916 22.75 10.8334ZM5.41669 8.7317L7.58335 8.85087V13.6717L5.41669 13.9425V8.7317ZM10.6384 20.5834H8.88336L8.03836 15.795L8.59086 15.73L9.89086 16.0875L10.6384 20.5834ZM17.3334 15.9034L11.4292 14.2675C11.2529 14.1491 11.0457 14.085 10.8334 14.0834L9.75002 13.78V8.6667L17.3334 5.91503V15.9034ZM19.5 12.6534V8.97003C19.8233 9.16188 20.0912 9.43455 20.2772 9.76124C20.4632 10.0879 20.5611 10.4574 20.5611 10.8334C20.5611 11.2093 20.4632 11.5788 20.2772 11.9055C20.0912 12.2322 19.8233 12.5049 19.5 12.6967V12.6534Z" fill="#666666"/>
                  </svg>
                  {unreadCount > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                <div className="dropdown-menu dropdown-menu-end" style={{ minWidth: '350px' }}>
                  <div className="dropdown-header d-flex justify-content-between">
                    <h4 className="mb-0">{t('notifications.title')}</h4>
                    {unreadCount > 0 && (
                      <button className="btn btn-sm btn-link p-0" onClick={handleMarkAllAsRead}>
                        {t('notifications.markAllAsRead')}
                      </button>
                    )}
                  </div>
                  <div className="p-3" style={{ maxHeight: '380px', overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-muted">{t('notifications.noNotifications')}</p>
                      </div>
                    ) : (
                      <ul className="list-unstyled">
                        {notifications.map((notif) => (
                          <li key={notif.Id} className={`mb-3 pb-3 ${notif.IsRead ? 'opacity-75' : ''}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <div style={{ cursor: 'pointer' }} onClick={() => handleNotificationClick(notif)}>
                              <h6 className="mb-1 fw-bold">{notif.Title}</h6>
                              <p className="mb-1 small">{notif.Message}</p>
                              <small className="text-muted">{formatNotificationTime(notif.Timestamp)}</small>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </li>

              {/* UserButton Clerk */}
              <SignedOut>
                <li className="nav-item">
                  <SignInButton mode="modal">
                    <button className="btn btn-primary">{t('user.signIn')}</button>
                  </SignInButton>
                </li>
              </SignedOut>

              <SignedIn>
                <li className="nav-item">
                  <UserButton
                    afterSignOutUrl="/"
                    userProfileMode="modal"
                    appearance={{
                      elements: {
                        avatarBox: "rounded-circle border border-2 border-primary"
                      }
                    }}
                  >
                    <UserButton.MenuItems>
                      <UserButton.Link
                        label={t('user.profile')}
                        labelIcon={<span>👤</span>}
                        href="/page/app/Component/admin/user_profile_page_view_trade_vault"
                      />
                    </UserButton.MenuItems>
                  </UserButton>
                </li>
              </SignedIn>
            </ul>
          </div>
        </nav>
      </div>
    </div>
  );
}

export default Header;